import { AxiosError, AxiosResponse } from 'axios';
import instance, { ApiError } from 'src/api';
import {
  ActiveHeadsetSubscription,
  Headset,
  HeadsetActivationCode,
  HeadsetComment,
  HeadsetEventsResponse,
  HeadsetInvoice,
  HeadsetMeta,
  HeadsetSubscription,
  HeadsetUserEvent,
  NewHeadsetComment,
} from 'src/models';

const headsetsApiPath = (id: string) => `companies/${id}/devices`;
export const getHeadsets = async ({
  companyId,
  query,
}: {
  companyId: string;
  query?: string;
  withoutIndication?: boolean;
  uuid: string;
}): Promise<AxiosResponse<Headset[] & ApiError>> => {
  return instance
    .get<Headset[]>(headsetsApiPath(companyId), { params: { query } })
    .catch((e) => e.response as AxiosError<ApiError> as never);
};

const headsetApiPath = (companyId: string, headsetId: string) =>
  `${headsetsApiPath(companyId)}/${headsetId}`;

export const getHeadset = async ({
  companyId,
  headsetId,
}: {
  companyId: string;
  headsetId: string;
}): Promise<AxiosResponse<Headset & ApiError>> => {
  return instance
    .get<Headset>(headsetApiPath(companyId, headsetId))
    .catch((e) => e.response as AxiosError<ApiError> as never);
};

export const updateHeadset = async ({
  companyId,
  headsetId,
  data,
}: {
  companyId: string;
  headsetId: string;
  data: Partial<Headset>;
}): Promise<AxiosResponse<Headset & ApiError>> => {
  return instance
    .post<Headset>(headsetApiPath(companyId, headsetId), data)
    .catch((e) => e.response as AxiosError<ApiError> as never);
};

export const deleteHeadsetAPI = async ({
  companyId,
  headsetId,
}: {
  companyId: string;
  headsetId: string;
}): Promise<AxiosResponse<void & ApiError>> => {
  return instance
    .delete(headsetApiPath(companyId, headsetId))
    .catch((e) => e.response as AxiosError<ApiError> as never);
};

const headsetPaymentsApiPath = (companyId: string, headsetId: string) =>
  `${headsetsApiPath(companyId)}/${headsetId}/payments`;

export const getHeadsetPayments = async ({
  companyId,
  headsetId,
}: {
  companyId: string;
  headsetId: string;
}): Promise<AxiosResponse<HeadsetInvoice[] & ApiError>> => {
  return instance
    .get<HeadsetInvoice[]>(headsetPaymentsApiPath(companyId, headsetId))
    .catch((e) => e.response as AxiosError<ApiError> as never);
};

const headsetActivationApiPath = (id: string) =>
  `${headsetsApiPath(id)}/activation_code`;

export const getHeadsetActivationCode = async ({
  companyId,
}: {
  companyId: string;
}): Promise<AxiosResponse<HeadsetActivationCode & ApiError>> => {
  return instance
    .get<HeadsetActivationCode>(headsetActivationApiPath(companyId))
    .catch((e) => e.response as AxiosError<ApiError> as never);
};

const headsetCancelActiveSessionApiPath = (
  companyId: string,
  headsetId: string,
) => `${headsetsApiPath(companyId)}/${headsetId}/session/cancel`;

export const headsetCancelActiveSession = async ({
  companyId,
  headsetId,
}: {
  companyId: string;
  headsetId: string;
}): Promise<AxiosResponse> => {
  return instance
    .post(headsetCancelActiveSessionApiPath(companyId, headsetId))
    .catch((e) => e.response as AxiosError<ApiError> as never);
};

const headsetSubscriptionsApiPath = (companyId: string, headsetId: string) =>
  `${headsetsApiPath(companyId)}/${headsetId}/subscriptions`;

export const getHeadsetSubscriptions = async ({
  companyId,
  headsetId,
}: {
  companyId: string;
  headsetId: string;
}): Promise<AxiosResponse<HeadsetSubscription[] & ApiError>> => {
  return instance
    .get<
      HeadsetSubscription[]
    >(headsetSubscriptionsApiPath(companyId, headsetId))
    .catch((e) => e.response as AxiosError<ApiError> as never);
};

const headsetUserEventsApiPath = (companyId: string, headsetId: string) =>
  `${headsetsApiPath(companyId)}/${headsetId}/user/events`;

export const getHeadsetUserEvents = async ({
  companyId,
  headsetId,
}: {
  companyId: string;
  headsetId: string;
}): Promise<AxiosResponse<HeadsetUserEvent[] & ApiError>> => {
  return instance
    .get<HeadsetUserEvent[]>(headsetUserEventsApiPath(companyId, headsetId))
    .catch((e) => e.response as AxiosError<ApiError> as never);
};

const headsetEventsApiPath = (companyId: string, headsetId: string) =>
  `${headsetsApiPath(companyId)}/${headsetId}/events`;

export const getHeadsetEvents = async ({
  companyId,
  headsetId,
  params,
}: {
  companyId: string;
  headsetId: string;
  params?: {
    start?: string;
    end?: string;
  };
}): Promise<AxiosResponse<HeadsetEventsResponse & ApiError>> => {
  return instance
    .get<HeadsetEventsResponse>(headsetEventsApiPath(companyId, headsetId), {
      params,
    })
    .catch((e) => e.response as AxiosError<ApiError> as never);
};

const headsetActivateSubscriptionApiPath = (
  companyId: string,
  headsetId: string,
) => `${headsetsApiPath(companyId)}/${headsetId}/subscriptions/activate`;

export const headsetActivateSubscription = async ({
  companyId,
  headsetId,
  data,
}: {
  companyId: string;
  headsetId: string;
  data: { packageId: string };
}): Promise<AxiosResponse<ActiveHeadsetSubscription & ApiError>> => {
  return instance
    .post<ActiveHeadsetSubscription>(
      headsetActivateSubscriptionApiPath(companyId, headsetId),
      data,
    )
    .catch((e) => e.response as AxiosError<ApiError> as never);
};

const headsetCancelAllActiveSubscriptionApiPath = (
  companyId: string,
  headsetId: string,
) => `${headsetsApiPath(companyId)}/${headsetId}/subscriptions/cancel/all`;

export const headsetCancelAllActiveSubscription = async ({
  companyId,
  headsetId,
}: {
  companyId: string;
  headsetId: string;
}): Promise<AxiosResponse<Headset & ApiError>> => {
  return instance
    .post<Headset>(
      headsetCancelAllActiveSubscriptionApiPath(companyId, headsetId),
    )
    .catch((e) => e.response as AxiosError<ApiError> as never);
};

const headsetCommentsApiPath = (companyId: string, headsetId: string) =>
  `${headsetsApiPath(companyId)}/${headsetId}/comments`;

export const getHeadsetCommentsAPI = async ({
  companyId,
  headsetId,
  params,
}: {
  companyId: string;
  headsetId: string;
  params?: {
    start?: string;
    end?: string;
  };
}): Promise<AxiosResponse<HeadsetComment[] & ApiError>> => {
  return instance
    .get<
      HeadsetComment[]
    >(headsetCommentsApiPath(companyId, headsetId), { params })
    .catch((e) => e.response as AxiosError<ApiError> as never);
};

export const addHeadsetCommentAPI = async ({
  companyId,
  headsetId,
  data,
}: {
  companyId: string;
  headsetId: string;
  data: NewHeadsetComment;
}): Promise<AxiosResponse<HeadsetComment & ApiError>> => {
  return instance
    .post<HeadsetComment>(headsetCommentsApiPath(companyId, headsetId), data)
    .catch((e) => e.response as AxiosError<ApiError> as never);
};

const headsetCommentApiPath = (
  companyId: string,
  headsetId: string,
  commentId: string,
) => `${headsetsApiPath(companyId)}/${headsetId}/comments/${commentId}`;

export const deleteHeadsetCommentAPI = async ({
  companyId,
  headsetId,
  commentId,
}: {
  companyId: string;
  headsetId: string;
  commentId: string;
}): Promise<AxiosResponse<void & ApiError>> => {
  return instance
    .delete(headsetCommentApiPath(companyId, headsetId, commentId))
    .catch((e) => e.response as AxiosError<ApiError> as never);
};

const headsetMetaApiPath = (companyId: string, headsetId: string) =>
  `${headsetsApiPath(companyId)}/${headsetId}/meta`;

export const getHeadsetMetaAPI = async ({
  companyId,
  headsetId,
}: {
  companyId: string;
  headsetId: string;
}): Promise<AxiosResponse<HeadsetMeta & ApiError>> => {
  return instance
    .get<HeadsetMeta>(headsetMetaApiPath(companyId, headsetId))
    .catch((e) => e.response as AxiosError<ApiError> as never);
};

export const deleteCustomerSessionAPI = async ({
  companyId,
  headsetId,
}: {
  companyId: string;
  headsetId: string;
}): Promise<AxiosResponse<{ result: boolean } & ApiError>> => {
  return instance
    .delete(`${headsetApiPath(companyId, headsetId)}/customer/session`)
    .catch((e) => e.response as AxiosError<ApiError> as never);
};
